import type { CommissionSightClient } from '@commissionsight/sdk';
import { useCallback } from 'react';
import { Badge, ErrorNote, Metric, Panel } from '../components/ui.js';
import { useAsyncData } from '../lib/useAsyncData.js';

const money = (cents: number) =>
  `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (n: number) => n.toLocaleString('en-US');
const pmLabel: Record<string, string> = {
  card: 'Card',
  us_bank_account: 'Bank (ACH)',
  none: 'No method',
};

export function Revenue({ client }: { client: CommissionSightClient }) {
  const { data, error, reload } = useAsyncData(useCallback(() => client.admin.revenue(), [client]));

  if (error)
    return (
      <Panel title="Revenue & billing">
        <ErrorNote message={error} onRetry={reload} />
      </Panel>
    );
  if (!data)
    return (
      <Panel title="Revenue & billing">
        <p className="text-sm text-neutral-500">Loading…</p>
      </Panel>
    );

  const { platform, revenue, ar, carriers } = data;

  return (
    <div>
      <Panel title="Revenue & billing">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <Metric
            label="Projected MRR"
            value={money(revenue.projectedMonthlyCents)}
            tone="emerald"
            hint="current members × pricing tiers"
          />
          <Metric
            label="Avg $ / member"
            value={money(revenue.avgCostPerMemberCents)}
            tone="indigo"
            hint="blended, actual pricing"
          />
          <Metric
            label="Last cycle billed"
            value={money(revenue.lastCycleBilledCents)}
            tone="sky"
            hint="sum of latest invoices"
          />
          <Metric
            label="Heartbeats monitored"
            value={num(platform.heartbeats)}
            tone="emerald"
            hint="distinct members, latest period"
          />
        </div>

        {/* Actual A/R from Stripe. */}
        <div className="mt-5">
          <div className="mb-2 text-xs font-medium uppercase tracking-wide text-neutral-500">
            Accounts receivable {ar.configured ? '· from Stripe' : ''}
          </div>
          {ar.configured ? (
            <>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                <Metric label="Billed" value={money(ar.billedCents)} tone="sky" />
                <Metric label="Paid" value={money(ar.paidCents)} tone="emerald" />
                <Metric label="Outstanding" value={money(ar.outstandingCents)} tone="amber" />
                <Metric
                  label="Failed / uncollectible"
                  value={money(ar.failedCents)}
                  tone={ar.failedCents > 0 ? 'rose' : 'neutral'}
                />
              </div>
              <p className="mt-2 text-xs text-neutral-400">
                Across {num(ar.invoices)} invoice{ar.invoices === 1 ? '' : 's'}
                {ar.recentOnly ? ' (most recent 100 — more exist)' : ''}.
              </p>
            </>
          ) : (
            <p className="rounded-lg border border-neutral-200/70 bg-white p-3 text-sm text-neutral-500">
              Stripe isn’t configured, so live A/R (paid / outstanding / failed) is unavailable. The
              figures above are derived from member counts and the last invoice run.
            </p>
          )}
        </div>

        {/* Revenue by payment method. */}
        <div className="mt-5">
          <div className="mb-2 text-xs font-medium uppercase tracking-wide text-neutral-500">
            By payment type
          </div>
          <table className="w-full text-sm">
            <thead className="text-left text-neutral-500">
              <tr>
                <th className="py-1">Method</th>
                <th>Accounts</th>
                <th>Projected MRR</th>
                <th>Last billed</th>
              </tr>
            </thead>
            <tbody>
              {(['card', 'us_bank_account', 'none'] as const).map((k) => (
                <tr key={k} className="border-t border-neutral-100">
                  <td className="py-2">
                    <Badge
                      tone={
                        k === 'card' ? 'indigo' : k === 'us_bank_account' ? 'emerald' : 'neutral'
                      }
                    >
                      {pmLabel[k]}
                    </Badge>
                  </td>
                  <td className="tabular-nums">{num(revenue.byPaymentType[k].accounts)}</td>
                  <td className="tabular-nums">{money(revenue.byPaymentType[k].projectedCents)}</td>
                  <td className="tabular-nums text-neutral-500">
                    {money(revenue.byPaymentType[k].lastBilledCents)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-1 text-xs text-neutral-400">
            {num(revenue.accountsWithPaymentMethod)} account
            {revenue.accountsWithPaymentMethod === 1 ? '' : 's'} have a payment method on file.
          </p>
        </div>
      </Panel>

      <div className="grid gap-6 lg:grid-cols-2">
        <Panel title="Platform">
          <div className="grid grid-cols-2 gap-4">
            <Metric
              label="Accounts"
              value={num(platform.accounts.total)}
              tone="emerald"
              hint={`${platform.accounts.active} active · ${platform.accounts.pending} pending`}
            />
            <Metric label="Users" value={num(platform.users)} tone="indigo" />
            <Metric label="Active API keys" value={num(platform.apiKeys)} tone="sky" />
            <Metric label="Carriers" value={num(platform.carriers)} tone="neutral" />
          </div>
        </Panel>

        <Panel title="By carrier">
          {carriers.length === 0 ? (
            <p className="text-sm text-neutral-500">No monitored members yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-neutral-500">
                <tr>
                  <th className="py-1">Carrier</th>
                  <th>Accounts</th>
                  <th>Heartbeats</th>
                </tr>
              </thead>
              <tbody>
                {carriers.map((c2) => (
                  <tr key={c2.carrierId} className="border-t border-neutral-100">
                    <td className="py-2 text-neutral-800">{c2.name}</td>
                    <td className="tabular-nums">{num(c2.accounts)}</td>
                    <td className="tabular-nums">{num(c2.heartbeats)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>
      </div>
    </div>
  );
}
